// CB:BEGIN fr-014-realtor-client-portal
/**
 * Public homeowner portal — realtor invite flow
 *
 * Flow: loading → preview (select bid) → confirm (signed-in homeowner) → project created
 * Auth: public for viewing; Clerk required for /accept
 *
 * Styling: Uses inline styles throughout for Tailwind v4 compatibility.
 * Tailwind utility classes are added as a supplementary layer only.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  brand:       '#123c3b',
  brandHover:  '#0d2b2a',
  brandLight:  '#f0faf9',
  brandBorder: '#a0c4c3',
  teal:        '#0f766e',
  white:       '#ffffff',
  // surfaces
  pageBg:      '#f3f4f6',
  surface:     '#ffffff',
  subtle:      '#f9fafb',
  // borders
  border:      '#e5e7eb',
  borderLight: '#f3f4f6',
  // text
  textPrimary:   '#111827',
  textSecondary: '#374151',
  textMuted:     '#6b7280',
  textFaint:     '#9ca3af',
  // semantic
  green50:   '#f0fdf4',
  green100:  '#dcfce7',
  green200:  '#bbf7d0',
  green600:  '#16a34a',
  green700:  '#15803d',
  green800:  '#166534',
  amber50:   '#fffbeb',
  amber100:  '#fef3c7',
  amber200:  '#fde68a',
  amber400:  '#fbbf24',
  amber800:  '#92400e',
  amber900:  '#78350f',
  blue50:    '#eff6ff',
  blue200:   '#bfdbfe',
  blue700:   '#1d4ed8',
  blue800:   '#1e40af',
  red50:     '#fef2f2',
  red200:    '#fecaca',
  red700:    '#b91c1c',
};

// Shared style objects
const card: React.CSSProperties = {
  background: C.surface,
  borderRadius: 16,
  border: `1px solid ${C.border}`,
  boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  overflow: 'hidden',
};

const cardElevated: React.CSSProperties = {
  ...card,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.05)',
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface PortalBid {
  id: string;
  contractorCompany: string | null;
  contractorBidCents: number | null;
  contractorBidFormatted: string | null;
  amountCents: number | null;
  amountFormatted: string | null;
  scopeOfWork: string | null;
  material: string | null;
  warrantyText: string | null;
  warrantyMaterialYears: number | null;
  warrantyLaborYears: number | null;
  timelineDays: number | null;
  roofSizeSqFt: number | null;
  roofSizeSquares: number | null;
  submittedAt: string | null;
  lineItems: Array<{ description: string; category: string; totalCents: number | null }>;
}

interface PortalPayload {
  quoteRequestId: string;
  homeownerName: string | null;
  address: string;
  city?: string;
  state?: string;
  bidCount: number;
  hasPdf: boolean;
  bidSnapshotAt: string;
  inviteExpiresAt: string;
  coBranding: {
    realtorName: string;
    realtorEmail: string;
    realtorPhone: string | null;
    realtorBrokerage: string | null;
  };
  bids: PortalBid[];
}

type Phase = 'loading' | 'preview' | 'redirect' | 'error';

type ErrorCode =
  | 'EXPIRED'
  | 'REVOKED'
  | 'ALREADY_USED'
  | 'NOT_FOUND'
  | 'INVALID_TOKEN'
  | 'WRONG_ROLE'
  | 'INTERNAL_ERROR';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function daysUntil(iso: string): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000));
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtTimeline(days: number | null): string | null {
  if (days == null) return null;
  return days === 1 ? '1 day' : `${days} days`;
}

function fmtSqFt(sqFt: number | null): string | null {
  if (sqFt == null) return null;
  return `${sqFt.toLocaleString()} sq ft`;
}

function getCanonicalInviteUrl(token: string): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/realtor/invite/${token}`;
}

// Badge computation ─────────────────────────────────────────────────────────

interface BidBadges { bestPrice: boolean; fastest: boolean; bestWarranty: boolean }

function computeBadges(bids: PortalBid[]): Map<string, BidBadges> {
  const map = new Map<string, BidBadges>();
  if (bids.length < 2) {
    bids.forEach((b) => map.set(b.id, { bestPrice: false, fastest: false, bestWarranty: false }));
    return map;
  }
  const prices    = bids.map(b => b.contractorBidCents).filter((v): v is number => v != null);
  const timelines = bids.map(b => b.timelineDays).filter((v): v is number => v != null);
  const warYears  = bids.map(b => b.warrantyMaterialYears ?? 0);
  const minPrice   = prices.length    > 0 ? Math.min(...prices)    : null;
  const minTime    = timelines.length > 0 ? Math.min(...timelines) : null;
  const maxWarranty = Math.max(...warYears);
  bids.forEach(b => map.set(b.id, {
    bestPrice:    minPrice   != null && b.contractorBidCents === minPrice,
    fastest:      minTime    != null && b.timelineDays       === minTime,
    bestWarranty: maxWarranty > 0    && (b.warrantyMaterialYears ?? 0) === maxWarranty,
  }));
  return map;
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{
      width: 40, height: 40, borderRadius: '50%',
      border: `4px solid ${C.border}`,
      borderTopColor: C.brand,
      animation: 'spin 0.7s linear infinite',
      margin: '0 auto',
    }} />
  );
}

// ─── Loading state ────────────────────────────────────────────────────────────

function LoadingState() {
  return (
    <div data-testid="realtor-portal-loading" style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: C.pageBg, gap: 16,
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Spinner />
      <p style={{ color: C.textMuted, fontSize: 14 }}>Loading your quotes…</p>
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────

function ErrorState({ code, message }: { code: ErrorCode; message: string }) {
  const cfg: Record<ErrorCode, { icon: string; title: string; body: string }> = {
    EXPIRED:        { icon: '⏱', title: 'This link has expired',      body: 'Contact your realtor for a new link.' },
    REVOKED:        { icon: '🚫', title: 'This link was cancelled',    body: 'Contact your realtor for a new link.' },
    ALREADY_USED:   { icon: '✅', title: 'Already activated',          body: 'Check your email for project details, or sign in to view your project.' },
    NOT_FOUND:      { icon: '🔗', title: 'Link not found',             body: 'Please check the link or contact your realtor.' },
    INVALID_TOKEN:  { icon: '🔗', title: 'Invalid link',               body: 'Please check the link in your email or contact your realtor.' },
    WRONG_ROLE:     { icon: '👤', title: 'Wrong account type',         body: message },
    INTERNAL_ERROR: { icon: '⚠️', title: 'Something went wrong',       body: 'Please try refreshing the page.' },
  };
  const c = cfg[code] ?? cfg.INTERNAL_ERROR;
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.pageBg, padding: 24 }}>
      <div style={{ ...card, padding: 40, maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{c.icon}</div>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: C.textPrimary, marginBottom: 12 }}>{c.title}</h1>
        <p style={{ color: C.textMuted, fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>{c.body}</p>
        {code === 'ALREADY_USED' && (
          <a href="/dashboard" style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: '12px 24px', background: C.brand, color: C.white,
            borderRadius: 12, fontSize: 14, fontWeight: 600, textDecoration: 'none',
          }}>
            Go to Dashboard
          </a>
        )}
        <p style={{ marginTop: 24, fontSize: 12, color: C.textFaint }}>
          Questions?{' '}
          <a href="mailto:support@corebids.com" style={{ color: C.teal, textDecoration: 'underline' }}>support@corebids.com</a>
        </p>
      </div>
    </div>
  );
}

// ─── Redirect success ─────────────────────────────────────────────────────────

function RedirectState() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.green50 }}>
      <div style={{ textAlign: 'center', padding: 32 }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: C.green100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto', marginBottom: 20, fontSize: 28,
        }}>✓</div>
        <p style={{ fontSize: 24, fontWeight: 700, color: C.green800, marginBottom: 8 }}>Project activated!</p>
        <p style={{ color: C.textMuted, fontSize: 14 }}>Redirecting to your project…</p>
      </div>
    </div>
  );
}

// ─── Bid card ─────────────────────────────────────────────────────────────────

function BidCard({
  bid, index, selected, badges, onSelect, token, hasPdf,
}: {
  bid: PortalBid; index: number; selected: boolean; badges: BidBadges;
  onSelect: (id: string) => void; token: string; hasPdf: boolean;
}) {
  const [downloading, setDownloading] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handlePdfDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloading(true);
    try {
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/portal/${token}/bids/${bid.id}/pdf`);
      if (!res.ok) { alert('Invoice not available for this quote.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `corebids-quote-${bid.id.slice(-8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download invoice. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const badgeList = [
    badges.bestPrice    && { label: 'Best Price',    bg: C.green100, color: C.green800 },
    badges.bestWarranty && { label: 'Best Warranty', bg: '#dbeafe', color: C.blue800 },
    badges.fastest      && { label: 'Fastest',       bg: C.amber100, color: C.amber800 },
  ].filter(Boolean) as { label: string; bg: string; color: string }[];

  const details = [
    bid.material        && { label: 'Material',       value: bid.material },
    bid.warrantyText    && { label: 'Warranty',        value: bid.warrantyText },
    fmtTimeline(bid.timelineDays) && { label: 'Timeline', value: fmtTimeline(bid.timelineDays)! },
    fmtSqFt(bid.roofSizeSqFt)    && { label: 'Roof Size', value: fmtSqFt(bid.roofSizeSqFt)! },
    bid.contractorBidFormatted   && { label: 'Contractor Bid', value: bid.contractorBidFormatted },
    bid.submittedAt              && { label: 'Submitted', value: fmtDate(bid.submittedAt) },
  ].filter(Boolean) as { label: string; value: string }[];

  const outerStyle: React.CSSProperties = {
    width: '100%',
    background: C.surface,
    borderRadius: 16,
    border: `2px solid ${selected ? C.green600 : hovered ? '#9ca3af' : C.border}`,
    boxShadow: selected || hovered ? '0 4px 12px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    padding: 0,
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    fontFamily: 'inherit',
  };

  const headerBg = selected ? C.green50 : C.brandLight;
  const headerBorderColor = selected ? C.green200 : C.borderLight;

  return (
    <button
      type="button"
      onClick={() => onSelect(bid.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={outerStyle}
    >
      {/* Card header: name + price */}
      <div style={{
        background: headerBg,
        borderBottom: `1px solid ${headerBorderColor}`,
        padding: '16px 20px',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Quote {index + 1}{bid.contractorCompany ? ` — ${bid.contractorCompany}` : ''}
            </p>
            {badgeList.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {badgeList.map(b => (
                  <span key={b.label} style={{
                    padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                    background: b.bg, color: b.color,
                  }}>
                    {b.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            {bid.amountFormatted ? (
              <>
                <p style={{ fontSize: 26, fontWeight: 800, color: C.textPrimary, margin: 0, lineHeight: 1 }}>
                  {bid.amountFormatted}
                </p>
              </>
            ) : (
              <p style={{ fontSize: 14, color: C.textMuted }}>Price pending</p>
            )}
          </div>
        </div>
      </div>

      {/* Details grid */}
      {details.length > 0 && (
        <div style={{
          padding: '16px 20px',
          background: C.surface,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '12px 24px',
        }}>
          {details.map(({ label, value }) => (
            <div key={label}>
              <p style={{ fontSize: 11, fontWeight: 600, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 2px 0' }}>
                {label}
              </p>
              <p style={{ fontSize: 14, color: C.textSecondary, margin: 0 }}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Scope of work */}
      {bid.scopeOfWork && (
        <div style={{
          padding: '12px 20px 16px',
          background: C.surface,
          borderTop: `1px solid ${C.borderLight}`,
        }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px 0' }}>
            Scope of Work
          </p>
          <p style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.6, margin: 0 }}>{bid.scopeOfWork}</p>
        </div>
      )}

      {/* PDF link */}
      {hasPdf && (
        <div style={{
          padding: '10px 20px',
          background: C.surface,
          borderTop: `1px solid ${C.borderLight}`,
          display: 'flex',
          alignItems: 'center',
        }}>
          <button
            type="button"
            onClick={handlePdfDownload}
            disabled={downloading}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 600, color: C.teal,
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              opacity: downloading ? 0.5 : 1, fontFamily: 'inherit',
            }}
          >
            <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            {downloading ? 'Opening…' : 'Download Quote Invoice'}
          </button>
        </div>
      )}

      {/* Selected indicator bar */}
      {selected && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 20px', background: C.green600,
        }}>
          <svg width={16} height={16} viewBox="0 0 20 20" fill={C.white} style={{ flexShrink: 0 }}>
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.white }}>Selected for your project</span>
        </div>
      )}
    </button>
  );
}

// ─── Primary button ───────────────────────────────────────────────────────────

function PrimaryButton({
  onClick, disabled, children, type = 'button',
}: {
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  type?: 'button' | 'submit';
}) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: '100%',
        padding: '14px 24px',
        background: disabled ? '#d1d5db' : hov ? C.brandHover : C.brand,
        color: C.white,
        border: 'none',
        borderRadius: 12,
        fontSize: 15,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s',
        fontFamily: 'inherit',
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  onClick, disabled, children,
}: {
  onClick?: () => void; disabled?: boolean; children: React.ReactNode;
}) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: '100%',
        padding: '12px 24px',
        background: hov ? C.subtle : C.white,
        color: C.textSecondary,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        fontSize: 14,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s',
        fontFamily: 'inherit',
        opacity: disabled ? 0.4 : 1,
        appearance: 'none' as const,
        WebkitAppearance: 'none' as const,
      }}
    >
      {children}
    </button>
  );
}

// ─── Auth panel (not signed in) ───────────────────────────────────────────────

function AuthPanel({
  token, selectedBid, openSignIn, openSignUp, hasSelection,
}: {
  token: string; selectedBid: PortalBid | null;
  openSignIn: (opts?: object) => void;
  openSignUp: (opts?: object) => void;
  hasSelection: boolean;
}) {
  const canonicalUrl = getCanonicalInviteUrl(token);
  const persist = () => {
    try {
      sessionStorage.setItem('cb.pendingInviteToken', token);
      if (selectedBid) sessionStorage.setItem('cb.pendingRealtorSelectedQuoteId', selectedBid.id);
    } catch {}
  };
  const handleSignUp = () => { persist(); openSignUp({ redirectUrl: canonicalUrl, unsafeMetadata: { portalToken: token, accountType: 'HOMEOWNER' } }); };
  const handleSignIn = () => { persist(); openSignIn({ redirectUrl: canonicalUrl }); };

  return (
    <div style={card}>
      {/* Selected summary */}
      {selectedBid && (
        <div style={{ padding: '14px 20px', background: C.green50, borderBottom: `1px solid ${C.green200}` }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: C.green700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 6px 0' }}>Your selection</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary, margin: 0 }}>
              {selectedBid.contractorCompany ?? 'Selected contractor'}
            </p>
            {selectedBid.amountFormatted && (
              <p style={{ fontSize: 20, fontWeight: 800, color: C.textPrimary, margin: 0 }}>
                {selectedBid.amountFormatted}
              </p>
            )}
          </div>
        </div>
      )}

      <div style={{ padding: 24 }}>
        <h3 style={{ fontSize: 17, fontWeight: 700, color: C.textPrimary, margin: '0 0 8px 0' }}>
          {hasSelection ? 'Create an account to lock in your selection' : 'Select a quote above to continue'}
        </h3>
        <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.6, margin: '0 0 20px 0' }}>
          {hasSelection
            ? 'Your selection is saved. Create a free CoreBids account to confirm — no payment required yet.'
            : 'Tap any quote card to select it, then create a free account to move forward.'}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PrimaryButton onClick={handleSignUp} disabled={!hasSelection}>
            {hasSelection ? 'Create a Free Account' : 'Select a quote to continue'}
          </PrimaryButton>
          <SecondaryButton onClick={handleSignIn} disabled={!hasSelection}>
            Sign In to Existing Account
          </SecondaryButton>
        </div>

        <p style={{ fontSize: 12, color: C.textFaint, textAlign: 'center', marginTop: 18 }}>
          Free to create · No payment required to view quotes
        </p>
      </div>
    </div>
  );
}

// ─── Wrong-role panel ─────────────────────────────────────────────────────────

function WrongRolePanel({ role, token, onSignOut, openSignUp }: {
  role: string; token: string; onSignOut: () => void; openSignUp: (opts?: object) => void;
}) {
  const labels: Record<string, string> = {
    CONTRACTOR: 'contractor', REALTOR: 'realtor', ADMIN: 'admin', PROPERTY_MANAGER: 'property manager',
  };
  const label = labels[role] ?? role.toLowerCase();
  const canonicalUrl = getCanonicalInviteUrl(token);
  const handleCreate = async () => {
    await onSignOut();
    try { sessionStorage.setItem('cb.pendingInviteToken', token); } catch {}
    openSignUp({ redirectUrl: canonicalUrl, unsafeMetadata: { portalToken: token, accountType: 'HOMEOWNER' } });
  };

  return (
    <div style={{ ...card, border: `1px solid ${C.amber200}` }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '14px 20px', background: C.amber50, borderBottom: `1px solid ${C.amber200}`,
      }}>
        <svg width={20} height={20} viewBox="0 0 20 20" fill={C.amber800} style={{ flexShrink: 0 }}>
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
        <p style={{ fontSize: 15, fontWeight: 700, color: C.amber900, margin: 0 }}>Homeowner account required</p>
      </div>
      <div style={{ padding: 20 }}>
        <p style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.6, margin: '0 0 18px 0' }}>
          You're currently signed in as a <strong>{label}</strong>. To accept these quotes and start your project,
          please sign out and continue with a homeowner account.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PrimaryButton onClick={handleCreate}>
            Sign out and create a homeowner account
          </PrimaryButton>
          <SecondaryButton onClick={onSignOut}>
            Sign out
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm step (signed-in homeowner) ──────────────────────────────────────

function ConfirmStep({
  payload, selectedBid, onAccept, accepting, acceptError,
}: {
  payload: PortalPayload; selectedBid: PortalBid;
  onAccept: (acknowledgedAt: string) => void; accepting: boolean; acceptError: string | null;
}) {
  const [agreed, setAgreed] = useState(false);
  const onSubmit = (e: React.FormEvent) => { e.preventDefault(); if (agreed) onAccept(new Date().toISOString()); };

  return (
    <div style={card}>
      {/* Selection summary */}
      <div style={{ padding: '18px 20px', background: C.green50, borderBottom: `1px solid ${C.green200}` }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: C.green700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 8px 0' }}>
          Confirm your selection
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 17, fontWeight: 700, color: C.textPrimary, margin: 0 }}>
            {selectedBid.contractorCompany ?? 'Selected contractor'}
          </p>
          {selectedBid.amountFormatted && (
            <p style={{ fontSize: 24, fontWeight: 800, color: C.textPrimary, margin: 0 }}>{selectedBid.amountFormatted}</p>
          )}
        </div>
        {selectedBid.timelineDays != null && (
          <p style={{ fontSize: 13, color: C.green700, marginTop: 6 }}>
            {fmtTimeline(selectedBid.timelineDays)} estimated
          </p>
        )}
      </div>

      <div style={{ padding: 20 }}>
        {/* Summary row */}
        <div style={{
          background: C.subtle, borderRadius: 10, padding: '12px 16px', marginBottom: 18,
        }}>
          {[
            { label: 'Property', value: payload.address },
            { label: 'Arranged by', value: payload.coBranding.realtorName },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
              <span style={{ color: C.textMuted }}>{label}</span>
              <span style={{ fontWeight: 600, color: C.textPrimary, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
            </div>
          ))}
        </div>

        <form onSubmit={onSubmit}>
          {/* Disclosures */}
          <div style={{
            background: C.blue50, border: `1px solid ${C.blue200}`, borderRadius: 10,
            padding: '14px 16px', marginBottom: 18,
          }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: C.blue800, margin: '0 0 10px 0' }}>
              Before you confirm, please acknowledge:
            </p>
            <ul style={{ fontSize: 13, color: C.blue700, lineHeight: 1.65, paddingLeft: 18, margin: 0 }}>
              <li style={{ marginBottom: 4 }}>The project details provided on my behalf are accurate.</li>
              <li style={{ marginBottom: 4 }}>These quotes were requested by my realtor on my behalf.</li>
              <li style={{ marginBottom: 4 }}>I authorize CoreBids to proceed with project setup.</li>
              <li>I am not yet obligated to proceed or make any payment.</li>
            </ul>
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginBottom: 18 }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              style={{ marginTop: 2, width: 16, height: 16, accentColor: C.brand, flexShrink: 0 }}
            />
            <span style={{ fontSize: 14, color: C.textSecondary, lineHeight: 1.5 }}>
              I have read and agree to the statements above.
            </span>
          </label>

          {acceptError && (
            <div style={{
              background: C.red50, border: `1px solid ${C.red200}`, borderRadius: 10,
              padding: '12px 16px', marginBottom: 16, fontSize: 14, color: C.red700,
            }}>
              {acceptError}
            </div>
          )}

          <PrimaryButton type="submit" disabled={!agreed || accepting}>
            {accepting ? 'Activating your project…' : 'Confirm & Start My Project'}
          </PrimaryButton>
        </form>
      </div>
    </div>
  );
}

// ─── Info list card ───────────────────────────────────────────────────────────

function InfoCard({ title, items }: {
  title: string;
  items: Array<{ icon: string | number; title: string; body: string; isNumber?: boolean }>;
}) {
  return (
    <div style={card}>
      <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.borderLight}` }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: C.textPrimary, margin: 0 }}>{title}</h3>
      </div>
      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {items.map(({ icon, title: t, body, isNumber }) => (
          <div key={t} style={{ display: 'flex', gap: 14 }}>
            {isNumber ? (
              <div style={{
                flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
                background: C.brandLight, border: `2px solid ${C.brandBorder}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 800, color: C.brand,
              }}>
                {icon}
              </div>
            ) : (
              <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{icon}</span>
            )}
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, margin: '0 0 3px 0' }}>{t}</p>
              <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6, margin: 0 }}>{body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const RealtorClientPortal: React.FC<{ token: string; onNavigate?: (screen: string) => void }> = ({
  token,
  onNavigate,
}) => {
  const { isSignedIn, getToken, isLoaded } = useAuth();
  const { user: _user } = useUser();
  const { openSignIn, openSignUp, signOut } = useClerk();

  const [phase, setPhase]             = useState<Phase>('loading');
  const [payload, setPayload]         = useState<PortalPayload | null>(null);
  const [errorCode, setErrorCode]     = useState<ErrorCode>('INTERNAL_ERROR');
  const [errorMsg, setErrorMsg]       = useState('');
  const [userRole, setUserRole]       = useState<string | null>(null);
  const [selectedBidId, setSelected]  = useState<string | null>(null);
  const [accepting, setAccepting]     = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Dev marker — confirms this component is rendering
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[CoreBids] RealtorClientPortal v3 rendering — token:', token?.slice(0, 8) + '…');
    }
  }, [token]);

  // ── Fetch portal data (public) ───────────────────────────────────────────
  useEffect(() => {
    if (!token) { setErrorCode('INVALID_TOKEN'); setPhase('error'); return; }
    const base = getApiBaseUrl().replace(/\/$/, '');
    fetch(`${base}/api/realtor/portal/${token}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setErrorCode((data.errorCode as ErrorCode) || 'INTERNAL_ERROR');
          setErrorMsg(data.userMessage || data.error || 'This link is unavailable.');
          setPhase('error');
        } else {
          const p = data as PortalPayload;
          setPayload(p);
          if (p.bids.length === 1) setSelected(p.bids[0].id);
          try {
            const saved = sessionStorage.getItem('cb.pendingRealtorSelectedQuoteId');
            if (saved && p.bids.some(b => b.id === saved)) setSelected(saved);
          } catch {}
          setPhase('preview');
          try { sessionStorage.removeItem('cb.pendingInviteToken'); } catch {}
        }
      })
      .catch(() => { setErrorCode('INTERNAL_ERROR'); setPhase('error'); });
  }, [token]);

  // ── Resolve role after sign-in ───────────────────────────────────────────
  useEffect(() => {
    if (!isLoaded || !isSignedIn || phase === 'loading' || phase === 'error') return;
    if (userRole !== null) return;
    const base = getApiBaseUrl().replace(/\/$/, '');
    getToken()
      .then(tok => fetch(`${base}/api/me`, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} }))
      .then(r => r.json())
      .then(d => setUserRole(d?.role ?? d?.user?.role ?? 'HOMEOWNER'))
      .catch(() => setUserRole('HOMEOWNER'));
  }, [isLoaded, isSignedIn, phase, userRole, getToken]);

  const handleSignOut   = useCallback(async () => { await signOut(); setUserRole(null); }, [signOut]);
  const handleSelectBid = useCallback((id: string) => {
    setSelected(id);
    try { sessionStorage.setItem('cb.pendingRealtorSelectedQuoteId', id); } catch {}
  }, []);

  const handleAccept = useCallback(async (acknowledgedAt: string) => {
    if (!selectedBidId) return;
    setAccepting(true); setAcceptError(null);
    try {
      const authToken = await getToken();
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/portal/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        body: JSON.stringify({ bidId: selectedBidId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.errorCode === 'WRONG_ROLE') { setUserRole(data.role ?? 'CONTRACTOR'); return; }
        setAcceptError(data.userMessage || data.error || 'Failed to activate. Please try again.');
        return;
      }
      try { sessionStorage.removeItem('cb.pendingRealtorSelectedQuoteId'); } catch {}
      setPhase('redirect');
      const projectId = data.project?.id ?? data.projectId;
      const dest = projectId ? `/projects/${projectId}` : '/dashboard';
      setTimeout(() => { if (onNavigate) onNavigate(dest); else window.location.href = dest; }, 900);
    } catch {
      setAcceptError('Network error. Please try again.');
    } finally {
      setAccepting(false);
    }
  }, [token, selectedBidId, getToken, onNavigate]);

  const handlePdfDownload = async () => {
    try {
      const base = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${base}/api/realtor/portal/${token}/pdf`);
      if (!res.ok) { alert('PDF not available.'); return; }
      const { url } = await res.json();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      alert('Failed to download PDF. Please try again.');
    }
  };

  // ── Render branches ───────────────────────────────────────────────────────

  if (phase === 'loading')  return <LoadingState />;
  if (phase === 'error')    return <ErrorState code={errorCode} message={errorMsg} />;
  if (phase === 'redirect') return <RedirectState />;
  if (!payload)             return <LoadingState />;

  const daysLeft       = daysUntil(payload.inviteExpiresAt);
  const isExpiringSoon = daysLeft <= 2;
  const selectedBid    = payload.bids.find(b => b.id === selectedBidId) ?? null;
  const isHomeowner    = isSignedIn && userRole === 'HOMEOWNER';
  const badgeMap       = computeBadges(payload.bids);

  const { realtorName, realtorEmail, realtorPhone } = payload.coBranding;

  return (
    <div
      data-testid="realtor-portal-v2"
      style={{ minHeight: '100vh', background: C.pageBg, fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif' }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header style={{ background: C.brand, padding: '14px 20px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: C.white, fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>CoreBids</span>
          <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>Secure homeowner portal</span>
        </div>
      </header>

      {/* ── Page content ───────────────────────────────────────────────────── */}
      <main style={{ maxWidth: 680, margin: '0 auto', padding: '28px 16px 40px', display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* ── Hero card ──────────────────────────────────────────────────── */}
        <div style={cardElevated}>
          {/* Green top band */}
          <div style={{ background: C.brand, padding: '24px 24px 20px' }}>
            {payload.homeownerName && (
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, margin: '0 0 4px 0' }}>
                Hello, <strong style={{ color: C.white }}>{payload.homeownerName}</strong>
              </p>
            )}
            <h1 style={{ color: C.white, fontSize: 26, fontWeight: 800, lineHeight: 1.2, margin: '0 0 6px 0' }}>
              Your roofing quotes are ready
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, margin: 0 }}>{payload.address}</p>

            {/* Meta chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 12, fontWeight: 600, color: C.white,
                background: 'rgba(255,255,255,0.18)', borderRadius: 999, padding: '5px 12px',
              }}>
                <svg width={13} height={13} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {payload.bidCount} quote{payload.bidCount !== 1 ? 's' : ''} ready
              </span>
              {isExpiringSoon ? (
                <span style={{
                  display: 'inline-flex', alignItems: 'center',
                  fontSize: 12, fontWeight: 700, background: C.amber400, color: C.amber900,
                  borderRadius: 999, padding: '5px 12px',
                }}>
                  ⚠ Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                </span>
              ) : (
                <span style={{
                  fontSize: 12, color: 'rgba(255,255,255,0.65)',
                  background: 'rgba(255,255,255,0.1)', borderRadius: 999, padding: '5px 12px',
                }}>
                  Available for {daysLeft} more day{daysLeft !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>

          {/* Presenter row */}
          <div style={{
            padding: '10px 24px', background: C.subtle,
            borderTop: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
          }}>
            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 6 }}>
                Presented by
              </span>
              <strong style={{ fontSize: 13, fontWeight: 600, color: C.textPrimary }}>{realtorName}</strong>
              <div style={{ display: 'flex', gap: 12, marginTop: 2, flexWrap: 'wrap' }}>
                {realtorEmail && <span style={{ fontSize: 12, color: C.textMuted }}>{realtorEmail}</span>}
                {realtorPhone && <span style={{ fontSize: 12, color: C.textMuted }}>{realtorPhone}</span>}
              </div>
            </div>
            <span style={{ fontSize: 12, color: C.textFaint }}>Powered by CoreBids</span>
          </div>
        </div>

        {/* ── PDF download ───────────────────────────────────────────────── */}
        {payload.hasPdf && (
          <div style={{ ...card, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, margin: '0 0 2px 0' }}>Quote Comparison PDF</p>
              <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>Formatted side-by-side comparison of all quotes</p>
            </div>
            <button
              onClick={handlePdfDownload}
              type="button"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '9px 18px', borderRadius: 10,
                border: `1.5px solid ${C.teal}`, background: C.white, color: C.teal,
                fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                fontFamily: 'inherit', appearance: 'none' as const,
              }}
            >
              <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              Download PDF
            </button>
          </div>
        )}

        {/* ── Quote cards ────────────────────────────────────────────────── */}
        {payload.bids.length > 0 && (
          <section>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: C.textPrimary, margin: 0 }}>
                {payload.bids.length === 1 ? 'Your quote' : `Compare ${payload.bids.length} quotes`}
              </h2>
              {!selectedBidId && payload.bids.length > 1 && (
                <span style={{
                  fontSize: 12, fontWeight: 700, color: C.amber800,
                  background: C.amber100, border: `1px solid ${C.amber200}`,
                  borderRadius: 999, padding: '4px 12px',
                }}>
                  Select one to continue →
                </span>
              )}
              {selectedBidId && payload.bids.length > 1 && (
                <span style={{
                  fontSize: 12, fontWeight: 700, color: C.green700,
                  background: C.green100, border: `1px solid ${C.green200}`,
                  borderRadius: 999, padding: '4px 12px',
                }}>
                  ✓ Quote selected
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {payload.bids.map((b, i) => (
                <BidCard
                  key={b.id}
                  bid={b}
                  index={i}
                  selected={b.id === selectedBidId}
                  badges={badgeMap.get(b.id) ?? { bestPrice: false, fastest: false, bestWarranty: false }}
                  onSelect={handleSelectBid}
                  token={token}
                  hasPdf={payload.hasPdf}
                />
              ))}
            </div>

            <p style={{ fontSize: 12, color: C.textFaint, textAlign: 'right', marginTop: 8 }}>
              Prices as of {fmtDate(payload.bidSnapshotAt)}
            </p>
          </section>
        )}

        {/* ── Auth / action area ─────────────────────────────────────────── */}
        {isLoaded && (
          <>
            {!isSignedIn && (
              <AuthPanel
                token={token}
                selectedBid={selectedBid}
                openSignIn={openSignIn}
                openSignUp={openSignUp}
                hasSelection={!!selectedBidId}
              />
            )}

            {isSignedIn && userRole === null && (
              <div style={{ ...card, padding: 24, textAlign: 'center' }}>
                <Spinner />
                <p style={{ fontSize: 14, color: C.textMuted, marginTop: 12 }}>Verifying your account…</p>
              </div>
            )}

            {isSignedIn && userRole !== null && userRole !== 'HOMEOWNER' && (
              <WrongRolePanel role={userRole} token={token} onSignOut={handleSignOut} openSignUp={openSignUp} />
            )}

            {isHomeowner && !selectedBidId && (
              <div style={{ ...card, padding: 24, textAlign: 'center' }}>
                <p style={{ fontSize: 14, color: C.textMuted, margin: 0 }}>Select a quote above to continue.</p>
              </div>
            )}

            {isHomeowner && selectedBid && (
              <ConfirmStep
                payload={payload}
                selectedBid={selectedBid}
                onAccept={handleAccept}
                accepting={accepting}
                acceptError={acceptError}
              />
            )}
          </>
        )}

        {/* ── What happens next ──────────────────────────────────────────── */}
        {!isHomeowner && (
          <InfoCard
            title="What happens next"
            items={[
              { icon: 1, isNumber: true, title: 'Create your free CoreBids account',  body: 'No payment required. Your quote selection is saved through sign-up.' },
              { icon: 2, isNumber: true, title: 'Confirm your selected quote',         body: 'Review the details and confirm — still no payment yet.' },
              { icon: 3, isNumber: true, title: 'Your contractor gets in touch',       body: 'Once activated, the selected contractor will schedule your project.' },
              { icon: 4, isNumber: true, title: 'Pay securely when work is complete', body: 'Payments are held in escrow and released by milestone — your money is protected.' },
            ]}
          />
        )}

        {/* ── About CoreBids ─────────────────────────────────────────────── */}
        <InfoCard
          title="About CoreBids"
          items={[
            { icon: '🏆', title: 'Competitive bidding',      body: 'Multiple licensed contractors compete for your project so you get fair, market-rate pricing.' },
            { icon: '🔒', title: 'Milestone-based payments', body: 'Your payment is held in escrow and only released when each phase of work is approved by you.' },
            { icon: '✓',  title: 'No pressure to decide now', body: 'Review at your own pace. No payment is required to view or compare quotes.' },
          ]}
        />

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <p style={{ textAlign: 'center', fontSize: 12, color: C.textFaint, paddingBottom: 8 }}>
          CoreBids · Secure homeowner portal ·{' '}
          <a href="mailto:support@corebids.com" style={{ color: C.teal, textDecoration: 'underline' }}>
            support@corebids.com
          </a>
        </p>

      </main>

      {/* Keyframe for spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default RealtorClientPortal;
// CB:END fr-014-realtor-client-portal
